import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useCartStore, syncTrackingAttributesToCart } from "@/stores/cartStore";
import { useLanguage } from "@/lib/i18n";
import { useTranslatedProductContent } from "@/hooks/useTranslatedProductContent";
import { calcDiscountPercent, createBuyNowCart, formatPrice, CURRENCY_TO_COUNTRY, type ShopifyProduct } from "@/lib/shopify";
import { getFixedPrice, formatFixedPrice } from "@/lib/fixedPrices";
import { trackInitiateCheckout, generateEventId } from "@/lib/facebookPixel";
import { trackGA4InitiateCheckout } from "@/lib/googleAnalytics";
import { getProductImages } from "@/lib/productImages";
import { ShoppingCart, Loader2, Sparkles, CreditCard, Minus, Plus } from "lucide-react";
import { toast } from "sonner";

interface ProductCardProps {
  product: ShopifyProduct;
}

export function ProductCard({ product }: ProductCardProps) {
  const { t, currency } = useLanguage();
  const productContent = useTranslatedProductContent(product.node.handle);
  const addItem = useCartStore(state => state.addItem);
  const isLoading = useCartStore(state => state.isLoading);
  const [isBuyingNow, setIsBuyingNow] = useState(false);
  const [quantity, setQuantity] = useState(1);

  const variant = product.node.variants.edges[0]?.node;
  const shopifyImage = product.node.images.edges[0]?.node;
  const localImages = getProductImages(product.node.handle);
  const heroImage = localImages.length > 0 ? localImages[0] : null;
  const isBundle = product.node.tags.includes('bundle');

  const displayCurrency = currency || 'USD';
  const fp = getFixedPrice(product.node.handle, displayCurrency);
  const isOnSale = fp ? fp.original > fp.sale : false;
  const discountPercent = fp ? calcDiscountPercent(fp.sale, fp.original) : 0;

  const handleAddToCart = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!variant) return;
    
    await addItem({
      product,
      variantId: variant.id,
      variantTitle: variant.title,
      price: variant.price,
      quantity,
      selectedOptions: variant.selectedOptions || []
    });
  };

  const handleBuyNow = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!variant) return;
    setIsBuyingNow(true);
    // Open blank window immediately to preserve user-gesture context (prevents popup blocking)
    const newWindow = window.open('about:blank', '_blank');
    try {
      const result = await createBuyNowCart(variant.id, quantity, CURRENCY_TO_COUNTRY[currency] || 'US');
      if (result) {
        // Final sync of tracking attributes before checkout redirect
        await syncTrackingAttributesToCart(result.cartId);

        // Fire InitiateCheckout tracking (CAPI + GA4) before redirect
        const price = fp?.sale || parseFloat(variant.price.amount);
        const checkoutData = {
          items: [{ productId: product.node.id, variantId: variant.id, title: product.node.title, price, quantity }],
          totalValue: price * quantity,
          currency: displayCurrency,
        };
        // Generate ONE shared event_id for both FB and GA4 — ensures dashboard dedup
        const icEventId = await generateEventId('InitiateCheckout', Date.now().toString());
        await Promise.all([
          trackInitiateCheckout(checkoutData, icEventId),
          trackGA4InitiateCheckout(checkoutData, icEventId),
        ]);

        if (newWindow) {
          newWindow.location.href = result.checkoutUrl;
        } else {
          window.location.href = result.checkoutUrl;
        }
      } else {
        newWindow?.close();
        toast.error(t('cart.error') || 'Something went wrong');
      }
    } catch (err) {
      console.error('Buy Now error:', err);
      newWindow?.close();
      toast.error(t('cart.error') || 'Something went wrong');
    } finally {
      setIsBuyingNow(false);
    }
  };

  return (
    <Card className="group overflow-hidden border-border hover:border-primary/50 transition-all duration-300 hover:shadow-lg bg-card shadow-soft flex flex-col h-full">
      <Link to={`/product/${product.node.handle}`} className="flex flex-col flex-1">
        <div className="relative aspect-square bg-secondary/30 overflow-hidden">
          {heroImage ? (
            <img
              src={heroImage.src}
              alt={t(heroImage.altKey)}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : shopifyImage ? (
            <img
              src={shopifyImage.url}
              alt={shopifyImage.altText || product.node.title}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-secondary">
              <Sparkles className="w-16 h-16 text-muted-foreground/40 animate-float" />
            </div>
          )}
          
          {/* Badges */}
          <div className="absolute top-3 right-3 flex flex-col items-end gap-2">
            {isBundle && (
              <Badge className="bg-primary text-primary-foreground border-0 shadow-md font-medium text-xs px-2 py-0.5 w-auto">
                ✨ {t('product.bestValue')}
              </Badge>
            )}
            {isOnSale && discountPercent > 0 && (
              <Badge className="bg-destructive text-destructive-foreground border-0 shadow-md font-medium text-xs px-2 py-0.5 w-auto">
                -{discountPercent}%
              </Badge>
            )}
          </div>
        </div>
        
        <CardContent className="px-4 py-5 bg-card flex flex-col flex-1">
          <h3 className="text-lg font-semibold text-foreground line-clamp-2 group-hover:text-primary transition-colors">
            {productContent.title || product.node.title}
          </h3>
          
          <p className="text-sm text-muted-foreground mt-2 line-clamp-2 leading-relaxed">
            {productContent.description || product.node.description}
          </p>

          {/* Stock indicator */}
          <div className="flex items-center gap-1.5 mt-2">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-success"></span>
            </span>
            <span className="text-xs font-medium text-success">{t('product.inStockReady')}</span>
          </div>
          
          <div className="mt-auto pt-4">
            <div className="flex items-center gap-3">
              {fp ? (
                <>
                  <span className="text-2xl font-extrabold text-foreground">
                    {formatFixedPrice(fp.sale, displayCurrency)}
                  </span>
                  {isOnSale && (
                    <span className="text-sm font-light text-muted-foreground line-through opacity-70">
                      {formatFixedPrice(fp.original, displayCurrency)}
                    </span>
                  )}
                </>
              ) : (
                <span className="text-2xl font-extrabold text-foreground">
                  {variant?.price && formatPrice(variant.price.amount, variant.price.currencyCode, currency)}
                </span>
              )}
            </div>

            {/* Quantity Selector */}
            <div className="flex items-center justify-between mt-3 w-full" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
              <span className="text-sm font-medium text-muted-foreground">{t('products.quantity')}:</span>
              <div className="flex items-center border border-border rounded-lg bg-background">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  aria-label="Decrease quantity"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setQuantity(Math.max(1, quantity - 1));
                  }}
                  disabled={quantity <= 1}
                >
                  <Minus className="w-3.5 h-3.5" />
                </Button>
                <span className="w-8 text-center font-medium text-sm">{quantity}</span>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  aria-label="Increase quantity"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setQuantity(quantity + 1);
                  }}
                >
                  <Plus className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>

            <div className="space-y-2 mt-3">
              <Button 
                onClick={handleAddToCart}
                disabled={isLoading || !variant?.availableForSale}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-bold"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : !variant?.availableForSale ? (
                  t('products.soldOut')
                ) : (
                  <>
                    <ShoppingCart className="w-4 h-4 me-2" />
                    {t('products.addToCart')}
                  </>
                )}
              </Button>

              <Button 
                variant="outline"
                onClick={handleBuyNow}
                disabled={isBuyingNow || !variant?.availableForSale}
                className="w-full border-primary text-primary hover:bg-primary/10 transition-colors font-bold"
              >
                {isBuyingNow ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 me-2" />
                    {t('products.buyNow')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </CardContent>
      </Link>
    </Card>
  );
}
